import { useQueries } from '@tanstack/react-query';
import { agendaApi, type EvenementAgenda } from '../../api/agenda.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { decouperPlage, type Jour } from '../../composants/calendrier/periodes.js';

/**
 * L'agenda sur une plage de jours quelconque. L'API refuse 92 jours ou plus :
 * l'année se lit en quatre requêtes, réunies ici. Un événement qui chevauche
 * deux morceaux (l'exposition de l'été) revient deux fois ; il n'en reste qu'un.
 * Même clé de cache que `useAgenda` pour chaque morceau : les rendez-vous
 * créés ou modifiés invalident tout d'un coup (`cleMetier(g, 'agenda')`).
 */
export function useAgendaPlage(
  galerie: string,
  plage: { du: Jour; au: Jour },
  sources?: readonly string[],
) {
  return useQueries({
    queries: decouperPlage(plage.du, plage.au).map((morceau) => {
      const periode = { ...morceau, ...(sources ? { sources } : {}) };
      return {
        queryKey: cleMetier(galerie, 'agenda', periode),
        queryFn: (contexte: Parameters<typeof appelDeRequete>[0]) =>
          agendaApi.lire(galerie, periode, appelDeRequete(contexte)),
      };
    }),
    combine: (resultats) => {
      const evenements = new Map<string, EvenementAgenda>();
      const sourcesLues = new Set<string>();
      let lu = false;
      for (const resultat of resultats) {
        if (!resultat.data) continue;
        lu = true;
        for (const evenement of resultat.data.evenements) evenements.set(evenement.id, evenement);
        for (const source of resultat.data.sources) sourcesLues.add(source);
      }
      return {
        data: lu ? { evenements: [...evenements.values()], sources: [...sourcesLues] } : undefined,
        isLoading: resultats.some((resultat) => resultat.isLoading),
        isError: resultats.some((resultat) => resultat.isError),
        error: resultats.find((resultat) => resultat.error)?.error,
      };
    },
  });
}
